import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, MailCheck } from "lucide-react";
import { CustomHeader } from "../components/AuthHeader";
import { CustomButton } from "../components/CustomButton";
import { useToast } from "../components/ToastProvider";
import { authService } from "../features/auth/authService";
import rushLogo from "../assets/png/rush-logo.png";

const inputClass =
  "w-full h-12 bg-gray-50 border border-gray-100 rounded-2xl px-5 text-gray-800 outline-none focus:border-[#23A9C6] focus:bg-white transition-all disabled:opacity-50";
const labelClass = "text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-2 block";

/**
 * Forgot password: 1) enter the account email and get a 6-digit code by email,
 * 2) enter the code and a new password.
 */
export default function ForgotPasswordScreen() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [step, setStep] = useState<"email" | "reset">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sentMessage, setSentMessage] = useState("");

  const sendCode = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      showToast("Enter a valid email address", "error");
      return;
    }
    setBusy(true);
    try {
      const message = await authService.requestPasswordReset(email.trim());
      setSentMessage(message);
      setStep("reset");
      showToast("Check your email for the code", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Couldn't send the code", "error");
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (code.length !== 6) {
      showToast("Enter the 6-digit code from the email", "error");
      return;
    }
    if (password.length < 8) {
      showToast("Your new password must be at least 8 characters", "error");
      return;
    }
    if (password !== confirm) {
      showToast("The passwords don't match", "error");
      return;
    }
    setBusy(true);
    try {
      const message = await authService.resetPassword(email.trim(), code, password);
      showToast(message || "Password reset. Please log in", "success");
      navigate("/login", { replace: true });
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Couldn't reset your password", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen md:min-h-0 bg-white px-6 pt-4 pb-10">
      <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-md px-6 -mx-6 pt-4 pb-4 flex items-center justify-between mb-4">
        <button
          onClick={() => (step === "reset" ? setStep("email") : navigate("/login"))}
          className="w-11 h-11 border border-gray-100 rounded-2xl flex items-center justify-center active:scale-95 transition-transform shadow-sm bg-white"
          aria-label="Back"
        >
          <ArrowLeft size={20} className="text-gray-700" />
        </button>
        <img src={rushLogo} alt="Rush Logo" className="h-7 w-auto object-contain" />
      </div>

      {step === "email" ? (
        <>
          <CustomHeader
            title="Forgot password?"
            subtitle="Enter the email you use for RUSH and we'll send you a code to reset your password."
          />
          <form
            className="mt-6 space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              void sendCode();
            }}
          >
            <div>
              <label className={labelClass} htmlFor="reset-email">
                Email Address
              </label>
              <input
                id="reset-email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                className={inputClass}
              />
            </div>
            <CustomButton
              type="submit"
              variant="primary"
              disabled={busy}
              className="w-full h-14 text-lg rounded-2xl font-bold shadow-lg shadow-cyan-100"
            >
              {busy ? "Sending..." : "Send code"}
            </CustomButton>
          </form>
        </>
      ) : (
        <>
          <CustomHeader title="Reset your password" subtitle={`Enter the code we sent to ${email.trim()}.`} />
          <div className="mt-4 flex gap-3 rounded-2xl bg-[#44B6CE]/10 px-4 py-3">
            <MailCheck size={18} className="text-[#1A8FAA] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700">
              {sentMessage || "We've sent you a 6-digit code."} It expires in 10 minutes. Check your spam folder if you
              can't see it.
            </p>
          </div>
          <form
            className="mt-6 space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              void reset();
            }}
          >
            <div>
              <label className={labelClass} htmlFor="reset-code">
                6-digit code
              </label>
              <input
                id="reset-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                value={code}
                maxLength={6}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                disabled={busy}
                className={`${inputClass} tracking-[0.5em] text-lg font-semibold`}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="reset-password">
                New password
              </label>
              <div className="relative">
                <input
                  id="reset-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                  className={inputClass}
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div>
              <label className={labelClass} htmlFor="reset-confirm">
                Confirm new password
              </label>
              <input
                id="reset-confirm"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="Type it again"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={busy}
                className={inputClass}
              />
            </div>
            <CustomButton
              type="submit"
              variant="primary"
              disabled={busy}
              className="w-full h-14 text-lg rounded-2xl font-bold shadow-lg shadow-cyan-100"
            >
              {busy ? "Saving..." : "Reset password"}
            </CustomButton>
            <p className="text-center text-sm text-gray-500">
              Didn&apos;t get it?{" "}
              <button
                type="button"
                onClick={() => void sendCode()}
                disabled={busy}
                className="text-[#23A9C6] font-bold hover:underline disabled:opacity-50"
              >
                Send a new code
              </button>
            </p>
          </form>
        </>
      )}
    </div>
  );
}
